/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Accordion } from "react-bootstrap";
import { propTypes } from "react-bootstrap/esm/Image";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";

const SidebarContent = ({ t, onNavigate }) => {
  const location = useLocation();
  const [openAccordion, setOpenAccordion] = useState(null);
  const menuWrapperRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const user = useSelector((state) => state.Login.user);
  const role = user ? user.role : "user";

  const {
    management,
    customer_revenue,
    customer_refund,
    general_bills,
    raw_material_bills,
    party_payment,
    staff_salary,
    cancel_booking,
    dashboards,
    lands,
  } = user || {};

  // Auto-scroll inside sidebar-menu-wrapper so opened accordion section and subitems are visible
  const scrollToAccordion = (key, previousKey = null) => {
    if (!menuWrapperRef.current) return;
    const container = menuWrapperRef.current;
    const targetElement = container.querySelector(
      `[data-accordion-id="${key}"]`
    );
    if (!targetElement) return;

    // Determine the header element to align with (category header if directly above)
    let scrollTarget = targetElement;
    const prevSibling = targetElement.previousElementSibling;
    if (
      prevSibling &&
      prevSibling.classList.contains("sidebar-category-header")
    ) {
      scrollTarget = prevSibling;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    const headerRect = scrollTarget.getBoundingClientRect();

    const SUBMENU_ESTIMATED_HEIGHTS = {
      dailyEntries: 95,
      plEntries: 295,
      users: 65,
      projectAdmin: 255,
      settings: 335,
    };

    const ACCORDION_ORDER = [
      "dailyEntries",
      "plEntries",
      "users",
      "projectAdmin",
      "settings",
    ];

    // If a previously open accordion above this one is collapsing, correct for height loss
    let heightCorrection = 0;
    if (
      previousKey &&
      previousKey !== key &&
      ACCORDION_ORDER.indexOf(previousKey) < ACCORDION_ORDER.indexOf(key)
    ) {
      const prevEl = container.querySelector(
        `[data-accordion-id="${previousKey}"]`
      );
      const prevBody =
        prevEl?.querySelector(".accordion-collapse") ||
        prevEl?.querySelector(".sidebar-submenu-list");
      if (prevBody && prevBody.offsetHeight > 0) {
        heightCorrection = prevBody.offsetHeight;
      } else {
        heightCorrection = SUBMENU_ESTIMATED_HEIGHTS[previousKey] || 0;
      }
    }

    const estimatedSubmenuHeight = SUBMENU_ESTIMATED_HEIGHTS[key] || 150;
    const estimatedBottom =
      targetRect.top - heightCorrection + 45 + estimatedSubmenuHeight;

    const isCutOffAtBottom = estimatedBottom > containerRect.bottom - 25;
    const isOffScreenTop =
      headerRect.top - heightCorrection < containerRect.top + 10;

    if (isCutOffAtBottom || isOffScreenTop) {
      const relativeTop =
        headerRect.top -
        containerRect.top +
        container.scrollTop -
        heightCorrection;
      const desiredScrollTop = Math.max(0, relativeTop - 12);

      if (typeof container.scrollTo === "function") {
        container.scrollTo({
          top: desiredScrollTop,
          behavior: "smooth",
        });
      } else {
        container.scrollTop = desiredScrollTop;
      }
    }
  };

  const handleAccordionToggle = (key) => {
    const isOpening = openAccordion !== key;
    const previousKey = openAccordion;
    setOpenAccordion(isOpening ? key : null);

    if (isOpening) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToAccordion(key, previousKey);
      }, 150);
    }
  };

  const isActiveRoute = (route) => location.pathname === route;

  const isAccordionChildActive = (routes) => routes.includes(location.pathname);

  const hasDashboardAccess =
    role === "superAdmin" ||
    lands ||
    dashboards ||
    customer_revenue ||
    party_payment ||
    raw_material_bills ||
    general_bills;

  const hasAdminAccess =
    role === "superAdmin" ||
    customer_revenue ||
    customer_refund ||
    staff_salary ||
    party_payment ||
    cancel_booking ||
    raw_material_bills ||
    general_bills;

  const dailyEntriesRoutes = ["/customer-revenue", "/project-expenses"];
  const plEntriesRoutes = [
    "/unit-incomes",
    "/customer-refund",
    "/material-expenses",
    "/general-expenses",
    "/party-expenses",
    "/cancel-booking",
    "/staff-salary",
  ];
  const usersRoutes = ["/users-list"];
  const projectAdminRoutes = [
    "/project-list",
    "/department-list",
    "/party-list",
    "/customer-list",
    "/employee-list",
    "/archived-project",
  ];
  const settingsRoutes = [
    "/project-configuration",
    "/unit-configuration",
    "/department-configuration",
    "/subdepartment-configuration",
    "/party-configuration",
    "/customer-configuration",
    "/employee-configuration",
    "/general-expense-configuration",
  ];

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const path = location.pathname;
    let matchingKey = null;
    if (dailyEntriesRoutes.includes(path)) {
      matchingKey = "dailyEntries";
    } else if (plEntriesRoutes.includes(path)) {
      matchingKey = "plEntries";
    } else if (usersRoutes.includes(path)) {
      matchingKey = "users";
    } else if (projectAdminRoutes.includes(path)) {
      matchingKey = "projectAdmin";
    } else if (settingsRoutes.includes(path)) {
      matchingKey = "settings";
    }

    if (matchingKey) {
      setOpenAccordion(matchingKey);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToAccordion(matchingKey);
      }, 200);
    }
  }, [location.pathname]);

  const handleMenuClick = (e) => {
    const link = e.target.closest("a");
    if (link && typeof onNavigate === "function") {
      onNavigate();
    }
  };

  return (
    <div
      ref={menuWrapperRef}
      className="sidebar-menu-wrapper"
      onClick={handleMenuClick}
    >
      <div className="d-flex flex-column">
        {hasDashboardAccess && (
          <div className="sidebar-category-header">{t("Dashboard")}</div>
        )}

        {(role === "superAdmin" || lands) && (
          <NavLink
            to="/lands"
            className={`sidebar-nav-link ${isActiveRoute("/lands") ? "active" : ""}`}
          >
            <i className="fas fa-box nav-icon"></i>
            <span className="nav-label">{t("Land Detail")}</span>
          </NavLink>
        )}

        {(role === "superAdmin" || dashboards) && (
          <NavLink
            to="/dashboard"
            className={`sidebar-nav-link ${isActiveRoute("/dashboard") ? "active" : ""}`}
          >
            <i className="fas fa-tachometer-alt nav-icon"></i>
            <span className="nav-label">{t("Dashboard")}</span>
          </NavLink>
        )}

        {(role === "superAdmin" || dashboards) && (
          <NavLink
            to="/booking-list"
            className={`sidebar-nav-link ${isActiveRoute("/booking-list") ? "active" : ""}`}
          >
            <i className="fas fa-clipboard-list nav-icon"></i>
            <span className="nav-label">{t("Booking List")}</span>
          </NavLink>
        )}

        {(role === "superAdmin" || dashboards) && (
          <NavLink
            to="/archived-booking-list"
            className={`sidebar-nav-link ${isActiveRoute("/archived-booking-list") ? "active" : ""}`}
          >
            <i className="fas fa-archive nav-icon"></i>
            <span className="nav-label">{t("Archived Booking List")}</span>
          </NavLink>
        )}

        {(role === "superAdmin" ||
          customer_revenue ||
          party_payment ||
          raw_material_bills ||
          general_bills) && (
          <Accordion
            activeKey={openAccordion}
            className="sidebar-accordion-item"
            data-accordion-id="dailyEntries"
          >
            <Accordion.Item eventKey="dailyEntries" style={{ border: "none" }}>
              <button
                type="button"
                className={`sidebar-accordion-btn ${
                  openAccordion === "dailyEntries" ? "open" : ""
                } ${
                  isAccordionChildActive(dailyEntriesRoutes)
                    ? "child-active"
                    : ""
                }`}
                onClick={() => handleAccordionToggle("dailyEntries")}
              >
                <i className="fas fa-folder-open nav-icon"></i>
                <span className="nav-label">{t("Daily Entries")}</span>
                <i className="fas fa-chevron-down accordion-chevron"></i>
              </button>
              <Accordion.Body className="p-0">
                <div className="sidebar-submenu-list">
                  {(customer_revenue || role === "superAdmin") && (
                    <NavLink
                      to="/customer-revenue"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/customer-revenue") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Revenue / Refund")}
                    </NavLink>
                  )}
                  {(raw_material_bills ||
                    party_payment ||
                    general_bills ||
                    role === "superAdmin") && (
                    <NavLink
                      to="/project-expenses"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/project-expenses") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Expenses")}
                    </NavLink>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {role === "superAdmin" && (
          <>
            <NavLink
              to="/daily-ledger"
              className={`sidebar-nav-link ${
                isActiveRoute("/daily-ledger") ? "active" : ""
              }`}
            >
              <i className="fas fa-book nav-icon"></i>
              <span className="nav-label">{t("Daily Ledger")}</span>
            </NavLink>
            <NavLink
              to="/interest-details"
              className={`sidebar-nav-link ${
                isActiveRoute("/interest-details") ? "active" : ""
              }`}
            >
              <i className="fas fa-hand-holding-usd nav-icon"></i>
              <span className="nav-label">{t("Interest Detail")}</span>
            </NavLink>
          </>
        )}

        {hasAdminAccess && (
          <div className="sidebar-category-header mt-2">{t("Admin")}</div>
        )}

        {(role === "superAdmin" ||
          customer_revenue ||
          customer_refund ||
          staff_salary ||
          party_payment ||
          cancel_booking ||
          raw_material_bills ||
          general_bills) && (
          <Accordion
            activeKey={openAccordion}
            className="sidebar-accordion-item"
            data-accordion-id="plEntries"
          >
            <Accordion.Item eventKey="plEntries" style={{ border: "none" }}>
              <button
                type="button"
                className={`sidebar-accordion-btn ${
                  openAccordion === "plEntries" ? "open" : ""
                } ${isAccordionChildActive(plEntriesRoutes) ? "child-active" : ""}`}
                onClick={() => handleAccordionToggle("plEntries")}
              >
                <i className="fas fa-file-invoice-dollar nav-icon"></i>
                <span className="nav-label">{t("P&L Entries")}</span>
                <i className="fas fa-chevron-down accordion-chevron"></i>
              </button>
              <Accordion.Body className="p-0">
                <div className="sidebar-submenu-list">
                  {(customer_revenue || role === "superAdmin") && (
                    <NavLink
                      to="/unit-incomes"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/unit-incomes") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Customer Revenue")}
                    </NavLink>
                  )}
                  {(customer_refund || role === "superAdmin") && (
                    <NavLink
                      to="/customer-refund"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/customer-refund") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Customer Refund")}
                    </NavLink>
                  )}
                  {(raw_material_bills || role === "superAdmin") && (
                    <NavLink
                      to="/material-expenses"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/material-expenses") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Raw Material Bills")}
                    </NavLink>
                  )}
                  {(general_bills || role === "superAdmin") && (
                    <NavLink
                      to="/general-expenses"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/general-expenses") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("General Bills")}
                    </NavLink>
                  )}
                  {(party_payment || role === "superAdmin") && (
                    <NavLink
                      to="/party-expenses"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/party-expenses") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Party Paid Bills")}
                    </NavLink>
                  )}
                  {(cancel_booking || role === "superAdmin") && (
                    <NavLink
                      to="/cancel-booking"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/cancel-booking") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Booking Cancelation")}
                    </NavLink>
                  )}
                  {(staff_salary || role === "superAdmin") && (
                    <NavLink
                      to="/staff-salary"
                      className={`sidebar-subnav-link ${
                        isActiveRoute("/staff-salary") ? "active" : ""
                      }`}
                    >
                      <span className="sub-dot"></span>
                      {t("Staff Salary")}
                    </NavLink>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {role === "superAdmin" && (
          <Accordion
            activeKey={openAccordion}
            className="sidebar-accordion-item"
            data-accordion-id="users"
          >
            <Accordion.Item eventKey="users" style={{ border: "none" }}>
              <button
                type="button"
                className={`sidebar-accordion-btn ${
                  openAccordion === "users" ? "open" : ""
                } ${isAccordionChildActive(usersRoutes) ? "child-active" : ""}`}
                onClick={() => handleAccordionToggle("users")}
              >
                <i className="fas fa-user-friends nav-icon"></i>
                <span className="nav-label">{t("Users")}</span>
                <i className="fas fa-chevron-down accordion-chevron"></i>
              </button>
              <Accordion.Body className="p-0">
                <div className="sidebar-submenu-list">
                  <NavLink
                    to="/users-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/users-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("All Users")}
                  </NavLink>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {(role === "superAdmin" || management) && (
          <div className="sidebar-category-header mt-2">{t("Setting")}</div>
        )}

        {(role === "superAdmin" || management) && (
          <Accordion
            activeKey={openAccordion}
            className="sidebar-accordion-item"
            data-accordion-id="projectAdmin"
          >
            <Accordion.Item eventKey="projectAdmin" style={{ border: "none" }}>
              <button
                type="button"
                className={`sidebar-accordion-btn ${
                  openAccordion === "projectAdmin" ? "open" : ""
                } ${
                  isAccordionChildActive(projectAdminRoutes)
                    ? "child-active"
                    : ""
                }`}
                onClick={() => handleAccordionToggle("projectAdmin")}
              >
                <i className="fas fa-book-reader nav-icon"></i>
                <span className="nav-label">{t("Project Administration")}</span>
                <i className="fas fa-chevron-down accordion-chevron"></i>
              </button>
              <Accordion.Body className="p-0">
                <div className="sidebar-submenu-list">
                  <NavLink
                    to="/project-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/project-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Projects")}
                  </NavLink>
                  <NavLink
                    to="/department-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/department-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Departments")}
                  </NavLink>
                  <NavLink
                    to="/party-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/party-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Parties")}
                  </NavLink>
                  <NavLink
                    to="/customer-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/customer-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Customers")}
                  </NavLink>
                  <NavLink
                    to="/employee-list"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/employee-list") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Employees")}
                  </NavLink>
                  <NavLink
                    to="/archived-project"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/archived-project") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Archived Projects")}
                  </NavLink>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {role === "superAdmin" && (
          <Accordion
            activeKey={openAccordion}
            className="sidebar-accordion-item"
            data-accordion-id="settings"
          >
            <Accordion.Item eventKey="settings" style={{ border: "none" }}>
              <button
                type="button"
                className={`sidebar-accordion-btn ${
                  openAccordion === "settings" ? "open" : ""
                } ${isAccordionChildActive(settingsRoutes) ? "child-active" : ""}`}
                onClick={() => handleAccordionToggle("settings")}
              >
                <i className="mdi mdi-table-large-plus nav-icon"></i>
                <span className="nav-label">{t("Settings")}</span>
                <i className="fas fa-chevron-down accordion-chevron"></i>
              </button>
              <Accordion.Body className="p-0">
                <div className="sidebar-submenu-list">
                  <NavLink
                    to="/project-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/project-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Project Settings")}
                  </NavLink>
                  <NavLink
                    to="/unit-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/unit-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Project Unit Settings")}
                  </NavLink>
                  <NavLink
                    to="/department-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/department-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Department Settings")}
                  </NavLink>
                  <NavLink
                    to="/subdepartment-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/subdepartment-configuration")
                        ? "active"
                        : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Sub Department Settings")}
                  </NavLink>
                  <NavLink
                    to="/party-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/party-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Party Settings")}
                  </NavLink>
                  <NavLink
                    to="/customer-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/customer-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Customer Settings")}
                  </NavLink>
                  <NavLink
                    to="/employee-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/employee-configuration") ? "active" : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("Employee Settings")}
                  </NavLink>
                  <NavLink
                    to="/general-expense-configuration"
                    className={`sidebar-subnav-link ${
                      isActiveRoute("/general-expense-configuration")
                        ? "active"
                        : ""
                    }`}
                  >
                    <span className="sub-dot"></span>
                    {t("General Expense Settings")}
                  </NavLink>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    </div>
  );
};

SidebarContent.propTypes = {
  t: propTypes.any,
};

export default withTranslation()(SidebarContent);
